/**
 * Parse the transaction specification out of a DNB Mastercard invoice PDF.
 *
 * The invoice contains the same transactions as the bank's xlsx statement, plus
 * detail the xlsx drops: a unique `Referanse` per row, the exact FX rate, and
 * per-transaction notes (flight passenger/route, EuroBonus points). Parsing it
 * here — rather than in the worker — is what the rest of the pipeline is built
 * around: the backend has no PDF reader, and the browser already extracts
 * invoice text for the auto-fill detectors.
 *
 * The output is a CSV in exactly the column schema the xlsx converter produces,
 * uploaded to the *same* importer as the xlsx. That is deliberate: the existing
 * overlap guard only looks for conflicting imports within one importer name, so
 * sharing it is what makes the invoice diff against the month's statement
 * instead of duplicating it.
 */
import { toCsv } from "./csv";

/** A parsed transaction row, before it becomes a CSV line. */
export type StatementRow = {
  /** `DD.MM.YYYY`, exactly as the invoice writes it. */
  date: string;
  /** Merchant and place, as the invoice's first detail line renders them. */
  merchant: string;
  /** Remaining detail lines, whitespace-collapsed and joined. */
  specification: string;
  /** The invoice's own reference number for the row (10 digits). */
  reference: string;
  /** Signed amount in NOK; negative is money out. */
  amount: number;
  /** Foreign amount when the row carries a `Beløp:`/`Kurs:` pair. */
  foreign: { amount: number; currency: string; rate: number } | null;
};

export type StatementTotals = {
  periodStart: string;
  periodEnd: string;
  /** "Sum bruk denne perioden" — spending only, payments excluded. */
  spent: number;
  /** "Sum innbetaling denne perioden". */
  paid: number;
};

export type ParsedStatement = {
  rows: StatementRow[];
  totals: StatementTotals;
};

/** Thrown when the text isn't a statement we can trust; the message is shown. */
export class StatementParseError extends Error {}

const collapse = (text: string): string => text.replace(/\s+/g, " ").trim();

/**
 * Parse a Norwegian-formatted amount (`-1.408,87`) into a number. The thousands
 * separator is a dot and the decimal separator a comma, so neither survives a
 * bare `parseFloat`.
 */
const parseAmount = (raw: string): number =>
  Number(raw.replace(/\./g, "").replace(",", "."));

/** Format a number back as the plain decimal the importer's parser expects. */
const formatDecimal = (value: number): string => value.toFixed(2);

const findAmount = (haystack: string, label: string): number => {
  const match = new RegExp(`${label}\\s+(-?[\\d.]*\\d,\\d{2})`).exec(haystack);
  if (!match) {
    throw new StatementParseError(
      `Couldn't find "${label}" in the invoice — is this a DNB Mastercard statement?`,
    );
  }
  return parseAmount(match[1]);
};

const parseTotals = (text: string): StatementTotals => {
  const flat = collapse(text);
  const period =
    /Periode\s+(\d{2}\.\d{2}\.\d{4})\s*-\s*(\d{2}\.\d{2}\.\d{4})/.exec(flat);
  if (!period) {
    throw new StatementParseError(
      "Couldn't find the statement period in the invoice.",
    );
  }
  return {
    periodStart: period[1],
    periodEnd: period[2],
    spent: findAmount(flat, "Sum bruk denne perioden"),
    paid: findAmount(flat, "Sum innbetaling denne perioden"),
  };
};

/**
 * The specification section, with the per-page table headers dropped. Anything
 * before "Fakturaspesifikasjon" is the summary page and anything from
 * "Økonomiske råd" on is the interest-table boilerplate — both carry dates and
 * amounts that would otherwise read as transactions.
 */
const specificationSection = (text: string): string => {
  const start = text.indexOf("Fakturaspesifikasjon");
  if (start === -1) {
    throw new StatementParseError(
      "This PDF has no transaction specification — DNB only includes one on the full invoice.",
    );
  }
  const rest = text.slice(start);
  const end = rest.search(/Økonomiske\s+r\s*å?\s*d/);
  return (end === -1 ? rest : rest.slice(0, end))
    .split("\n")
    .filter(
      (line) =>
        !/^\s*Oversikt over transaksjoner/.test(line) &&
        !/^\s*Bruksdato\s/.test(line),
    )
    .join("\n");
};

// A row runs from its date to its reference + amount. The body in between is
// the merchant line plus any detail lines, so it may span newlines. The
// reference is bounded by whitespace and a non-digit so a 13-digit
// `Billettnummer:` can't be mistaken for one.
const ROW_PATTERN =
  /(\d{2}\.\d{2}\.\d{4})\s+([\s\S]*?)\s(\d{10})(?!\d)\s+(-?[\d.]*\d,\d{2})/g;

const FOREIGN_PATTERN =
  /Beløp:\s*([\d.]*\d,\d{2})\s*([A-Z]{3})[\s\S]*?Kurs:\s*([\d.]*\d,\d+)/;

const parseRow = (
  date: string,
  body: string,
  reference: string,
  amount: string,
): StatementRow => {
  // A second date opening a line inside the body means the previous row had no
  // reference and we swallowed the next one — refuse rather than merge them.
  if (/\n\s*\d{2}\.\d{2}\.\d{4}\s/.test(body)) {
    throw new StatementParseError(
      `Couldn't tell where the transaction on ${date} ends — the invoice layout looks unfamiliar.`,
    );
  }

  const [merchantLine = "", ...detailLines] = body.split("\n");
  const foreignMatch = FOREIGN_PATTERN.exec(body);

  return {
    date,
    merchant: collapse(merchantLine),
    specification: collapse(detailLines.join(" ")),
    reference,
    amount: parseAmount(amount),
    foreign: foreignMatch
      ? {
          amount: parseAmount(foreignMatch[1]),
          currency: foreignMatch[2],
          rate: parseAmount(foreignMatch[3]),
        }
      : null,
  };
};

/**
 * Parse `text` (as produced by `extractPdfText`) into rows plus the invoice's
 * own control totals, and verify one against the other: every row of the period
 * has to add up to the spending and payment sums DNB printed on the summary
 * page. A layout change that made us silently miss rows would otherwise import
 * a plausible-looking, incomplete month.
 */
export const parseStatement = (text: string): ParsedStatement => {
  const totals = parseTotals(text);
  const section = specificationSection(text);

  const rows: StatementRow[] = [];
  for (const match of section.matchAll(ROW_PATTERN)) {
    rows.push(parseRow(match[1], match[2], match[3], match[4]));
  }

  if (rows.length === 0) {
    throw new StatementParseError(
      "Found the transaction specification but no transactions in it.",
    );
  }

  const expected = totals.spent + totals.paid;
  const sum = rows.reduce((total, row) => total + row.amount, 0);
  // Round to øre before comparing: the sum of many two-decimal values drifts in
  // binary floating point well below a øre, which is not a discrepancy.
  if (Math.round(sum * 100) !== Math.round(expected * 100)) {
    throw new StatementParseError(
      `The ${rows.length} transactions read from this invoice add up to ${formatDecimal(sum)}, ` +
        `but the invoice says ${formatDecimal(expected)}. Nothing was imported.`,
    );
  }

  return { rows, totals };
};

/**
 * The header the DNB Mastercard importer reads. `Spesifikasjon` stays in the
 * file as a record of the invoice, but the importer ignores it (config v4):
 * the FX line duplicates `Valuta`/`Kurs`, and the rest is noise in a list.
 */
const CSV_HEADER = [
  "Dato",
  "Beløpet gjelder",
  "Valuta",
  "Kurs",
  "Inn",
  "Ut",
  "Referanse",
  "Spesifikasjon",
] as const;

/**
 * Render parsed rows as the CSV the DNB Mastercard importer reads.
 *
 * `Inn`/`Ut` split the signed amount the way the bank's own export does, and
 * the foreign amount is written without a thousands separator because the
 * importer's `Valuta` regex reads digits and commas only — `1.408,87 SEK` would
 * otherwise be picked up as `408,87`.
 */
export const statementToCsv = (rows: readonly StatementRow[]): string =>
  toCsv([
    [...CSV_HEADER],
    ...rows.map((row) => [
      row.date,
      row.merchant,
      row.foreign
        ? `${row.foreign.amount.toFixed(2).replace(".", ",")} ${row.foreign.currency}`
        : "",
      row.foreign ? String(row.foreign.rate) : "",
      row.amount > 0 ? formatDecimal(row.amount) : "",
      row.amount < 0 ? formatDecimal(-row.amount) : "",
      row.reference,
      row.specification,
    ]),
  ]);
